import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { Checkbox, ErrorMsg } from '@app/form';
import {
  Criterion,
  CustomSelectChoice,
  FieldChoice,
  FieldType,
  Quota,
  SelectChoiceField,
} from 'queries';
import { isAdminSelector } from 'store';
import { ChoiceBuilder } from '../elements/ChoiceBuilder';
import { Trans, useTranslation } from 'react-i18next';
import { acceptChoice, criterionHandleOthers } from 'lib/criteria';
import { LogicJumpOption } from '../elements/interface';

import { useOnLogicJumpChange } from '../../hooks/useOnLogicJumpChange';
import { useCriterionChoiceProps } from '../lib/useCriterionChoiceProps';
import { LogicAndValidationChoiceButtons } from '../elements/LogicAndValidationChoiceButtons';
import { findJump } from '../lib/findJump';
import { FieldQuota } from '../elements/FieldQuota';
import { useAddQuotaButtonHandlerField } from '../lib/useFieldQuotaHooks';
import { ChoiceTagButton } from '../elements/ChoiceTagButton';
import { AddQuotaButton, InformationBox } from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.grayBackground,
  },
  questions: {
    paddingLeft: theme.marginBase * 2,
    paddingRight: theme.marginBase * 2,
    paddingBottom: theme.marginBase * 2,
  },
  choices: {
    display: 'flex',
    backgroundColor: theme.colors.white,
    padding: theme.marginBase * 2,
    gap: theme.marginBase * 2,
  },
  choiceContent: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.marginBase,
  },
  choiceContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  label: {
    ...theme.fonts.label,
    marginBottom: theme.marginBase / 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  infoAddChoice: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
    marginTop: theme.marginBase * 2,
  },
  warning: {
    marginBottom: theme.marginBase * 2,
  },
}));

interface Props<T> {
  field: T;
  onChange: (field: T) => any;
  criterion?: Criterion;
  onCriterionChange?: (criterion: Criterion) => any;
  logicJumpOptions?: LogicJumpOption[];
  children?: React.ReactNode;
  mustChooseValidation?: boolean;
  enabledTolerate?: boolean;
  numberOfParticipantRequired?: number;
  sessionId?: string;
  quotas?: Quota[] | null;
  onChangeQuota?: (quotas: Quota[]) => void;
}

export const SelectChoiceBuilder = <T extends SelectChoiceField>({
  field,
  onChange,
  criterion,
  onCriterionChange,
  logicJumpOptions,
  children,
  mustChooseValidation,
  enabledTolerate,
  numberOfParticipantRequired,
  sessionId,
  quotas,
  onChangeQuota,
}: Props<T>) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const [newIndex, setNewIndex] = useState(-1);
  const choices = useMemo(
    () => (field.properties ? field.properties.choices || [] : []),
    [field],
  );
  const isAdmin = useSelector(isAdminSelector);
  const { t } = useTranslation();
  const criterionQuotas = quotas?.filter((q) => q.ref === criterion?.fieldRef);
  const hasQuotas = !!criterionQuotas?.length;

  const allowOtherChoice = useMemo(() => {
    return choices.filter((c) => c.ref === CustomSelectChoice.Other).length > 0;
  }, [choices]);

  const allowNoneChoice = useMemo(() => {
    return choices.filter((c) => c.ref === CustomSelectChoice.None).length > 0;
  }, [choices]);

  const _onCriterionChange = useCallback(
    (newCriterion: Criterion) => {
      if (onCriterionChange) {
        onCriterionChange(newCriterion);
      }

      if (onChangeQuota) {
        const otherQuotas =
          quotas?.filter((q) => q.ref !== criterion?.fieldRef) || [];

        onChangeQuota([...otherQuotas, ...(newCriterion?.quotas || [])]);
      }
    },
    [onCriterionChange, onChangeQuota, quotas, criterion],
  );

  const onChoicesChange = useCallback(
    (newChoices: FieldChoice[]) => {
      onChange({
        ...field,
        properties: {
          ...field.properties,
          choices: newChoices,
        },
      });
    },
    [field, onChange],
  );

  const {
    isChoiceDeclined,
    isChoiceAccepted,
    isChoiceTolerated,
    acceptAllChoices,
    onAcceptClick,
    onDeclineClick,
    onTolerateClick,
  } = useCriterionChoiceProps({
    field,
    criterion,
    quotas: criterionQuotas,
    onCriterionChange: _onCriterionChange,
    enabledTolerate,
  });

  useEffect(() => {
    if (!criterion && mustChooseValidation) {
      acceptAllChoices();
    }
  }, [
    acceptAllChoices,
    choices,
    criterion,
    mustChooseValidation,
    onAcceptClick,
  ]);

  useEffect(() => {
    if (!hasQuotas || !_onCriterionChange) {
      return;
    }

    const availableRefs = choices.map((c) => c.ref);
    const cleanedQuotas = criterionQuotas?.filter((quota) =>
      availableRefs.find((r) => String(r) === String(quota.item)),
    );

    if (criterion && cleanedQuotas?.length !== criterionQuotas?.length) {
      _onCriterionChange({
        ...criterion,
        quotas: cleanedQuotas,
      });
    }
  }, [criterion, choices, _onCriterionChange, hasQuotas, criterionQuotas]);

  const onAddChoice = useCallback(
    (index: number, customChoice?: CustomSelectChoice) => {
      const newChoices = [...choices];
      let ref = uuidv4();
      let item = { label: '', ref };

      const countCustomChoice = [allowNoneChoice, allowOtherChoice].filter(
        (c) => c,
      ).length;
      if (index >= choices.length - countCustomChoice && !customChoice) {
        index -=
          countCustomChoice -
          ((choices.length - 1 - index) % countCustomChoice);
      }

      if (customChoice) {
        switch (customChoice) {
          case CustomSelectChoice.Other:
            allowNoneChoice && index--;
            item = {
              label: t('screeningForm.other'),
              ref: CustomSelectChoice.Other,
            };
            ref = CustomSelectChoice.Other;
            break;
          case CustomSelectChoice.None:
            item = {
              label: t('screeningForm.none'),
              ref: CustomSelectChoice.None,
            };
            ref = CustomSelectChoice.None;
            break;
        }
      }

      newChoices.splice(index + 1, 0, item);
      onChoicesChange(newChoices);
      setNewIndex(index + 1);

      if (mustChooseValidation) {
        onAcceptClick(item);
      } else if (_onCriterionChange && criterion) {
        const cri = acceptChoice(criterion, item);
        if (
          hasQuotas &&
          !cri?.quotas?.find((quota) => String(quota.item) === String(item.ref))
        ) {
          cri?.quotas?.push({ item: item.ref, value: 0 });
        }

        _onCriterionChange(cri);
      }
    },
    [
      choices,
      allowNoneChoice,
      allowOtherChoice,
      onChoicesChange,
      mustChooseValidation,
      _onCriterionChange,
      criterion,
      t,
      onAcceptClick,
      hasQuotas,
    ],
  );

  const onRemove = useCallback(
    (index: number) => {
      const choiceRef = choices[index].ref;
      onChoicesChange(choices.filter((_: any, i: number) => i !== index));
      criterion &&
        _onCriterionChange &&
        _onCriterionChange({
          ...criterion,
          quotas: criterionQuotas?.filter(
            (quota) => String(quota.item) !== String(choiceRef),
          ),
        });
    },
    [choices, onChoicesChange, criterion, _onCriterionChange, criterionQuotas],
  );

  const onChoiceChange = useCallback(
    (index: number, choice: FieldChoice) => {
      const newChoices = [...choices];
      newChoices[index] = choice;
      onChoicesChange(newChoices);
    },
    [onChoicesChange, choices],
  );

  const handleQuotasButton = useAddQuotaButtonHandlerField({
    hasQuotas,
    onChoicesChange,
    choices,
    onCriterionChange: _onCriterionChange,
    criterion,
    numberOfParticipantRequired,
  });

  const quotaIsDisabled = useCallback(
    (choice: FieldChoice) => {
      return !criterionQuotas?.find(
        (c) => String(c.item) === String(choice.ref),
      );
    },
    [criterionQuotas],
  );

  const sum = criterionQuotas?.reduce(
    (acc, field) => acc + (field.value || 0),
    0,
  );
  const isInvalidMessage =
    sum !== numberOfParticipantRequired
      ? (sum ?? 0) > (numberOfParticipantRequired ?? 0)
        ? t('editSession.targeting.quotas.overly', {
            count: sum,
            required: numberOfParticipantRequired,
          })
        : t('editSession.targeting.quotas.insufficient', {
            count: sum,
            required: numberOfParticipantRequired,
          })
      : false;

  const onLogicJumpOptionChange = useOnLogicJumpChange(field, onChange);

  return (
    <div className={classes.container}>
      <div className={classes.questions}>
        <div className={classes.label}>
          <Trans i18nKey="screeningForm.create.questionBuilder.choiceList.label" />
          <AddQuotaButton value={hasQuotas} onClick={handleQuotasButton} />
        </div>
        <div className={classes.choiceContainer}>
          {choices.map((choice: FieldChoice, index: number) => {
            const jump = field.jumpActions?.find((j) =>
              j.conditionChoiceRefs?.includes(choice.ref),
            );
            const selectedLogicJumpOption = findJump(logicJumpOptions, jump);
            return (
              <div className={classes.choiceContent} key={choice.ref}>
                <ChoiceBuilder
                  label={
                    index === 0
                      ? 'screeningForm.create.questionBuilder.choiceList.label'
                      : ''
                  }
                  placeholder={t(
                    'screeningForm.create.questionBuilder.choiceList.placeholder',
                  )}
                  isDeletable={choices.length > 1}
                  index={index}
                  autoFocus={newIndex === index}
                  onRemove={onRemove}
                  onPressEnter={onAddChoice}
                  onChange={onChoiceChange}
                  choice={choice}
                />
                <LogicAndValidationChoiceButtons
                  logicJumpOptions={logicJumpOptions}
                  onLogicJumpOptionChange={onLogicJumpOptionChange}
                  selectedLogicJumpOption={selectedLogicJumpOption}
                  choice={choice}
                  onAcceptClick={onAcceptClick}
                  onDeclineClick={onDeclineClick}
                  onTolerateClick={
                    enabledTolerate ? onTolerateClick : undefined
                  }
                  declined={isChoiceDeclined(choice)}
                  accepted={isChoiceAccepted(choice)}
                  tolerated={isChoiceTolerated(choice)}
                  mustChooseValidation={mustChooseValidation}
                />
                <ChoiceTagButton
                  onChange={onChoiceChange}
                  choice={choice}
                  index={index}
                  sessionId={sessionId}
                />
                {hasQuotas && (
                  <FieldQuota
                    key={`quota_${choice.ref}`}
                    choice={choice.ref}
                    criterion={criterion}
                    quota={quotas?.find(
                      (c) => String(c.item) === String(choice.ref),
                    )}
                    quotas={criterionQuotas}
                    onChange={_onCriterionChange}
                    disabled={quotaIsDisabled(choice)}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className={classes.infoAddChoice}>
          {hasQuotas && field.type === FieldType.MULTIPLE_CHOICE && (
            <ErrorMsg
              variant="warn"
              className={classes.warning}
              error={t('editSession.targeting.quotas.multiChoice')}
            />
          )}
          {hasQuotas && !!isInvalidMessage && (
            <ErrorMsg
              variant="warn"
              className={classes.warning}
              error={isInvalidMessage}
            />
          )}
          <Trans i18nKey="screeningForm.create.questionBuilder.choiceList.hintPlaceholder" />
        </div>
      </div>
      <div className={classes.choices}>
        {criterionHandleOthers(criterion) && (
          <>
            <div>
              <Checkbox
                label={t(
                  'screeningForm.create.questionBuilder.choiceList.otherChoiceDisplayed',
                )}
                checked={allowOtherChoice}
                noMargin
                onChange={() => {
                  if (!allowOtherChoice) {
                    onAddChoice(choices.length - 1, CustomSelectChoice.Other);
                  } else {
                    onChoicesChange(
                      choices.filter((c) => c.ref !== CustomSelectChoice.Other),
                    );
                  }
                }}
              />
            </div>
            <div>
              <Checkbox
                label={t(
                  'screeningForm.create.questionBuilder.choiceList.noneChoiceDisplayed',
                )}
                checked={allowNoneChoice}
                noMargin
                onChange={() => {
                  if (!allowNoneChoice) {
                    onAddChoice(choices.length - 1, CustomSelectChoice.None);
                  } else {
                    onChoicesChange(
                      choices.filter((c) => c.ref !== CustomSelectChoice.None),
                    );
                  }
                }}
              />
            </div>
            {isAdmin && (
              <div>
                <Checkbox
                  label={t(
                    'screeningForm.create.questionBuilder.choiceList.autocomplete',
                  )}
                  checked={field.properties.autocomplete}
                  noMargin
                  onChange={() =>
                    onChange({
                      ...field,
                      properties: {
                        ...field.properties,
                        autocomplete: !field.properties.autocomplete,
                      },
                    })
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
      {children}
    </div>
  );
};
